import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Shield, Users } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

const formatDate = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '—' : date.toLocaleDateString();
};

const AdminUsersPage = () => {
  const { user, loading: authLoading, logout } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (authLoading || !user || user.role !== 'admin') {
      setLoading(false);
      return undefined;
    }

    let active = true;
    fetch(`${API_BASE}/api/admin/users`, { credentials: 'include' })
      .then(async (response) => {
        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
          throw new Error(data.detail || 'Could not load users.');
        }
        if (active) setUsers(data);
      })
      .catch((fetchError) => {
        if (active) setError(fetchError.message || 'Could not load users.');
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [authLoading, user]);

  const filteredUsers = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return users;
    return users.filter((account) =>
      [account.name, account.email, account.role, account.id]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(query))
    );
  }, [search, users]);

  if (authLoading) {
    return <main className="min-h-screen bg-gray-50 px-4 pt-28 text-center text-gray-600">Checking administrator access…</main>;
  }

  if (!user || user.role !== 'admin') {
    return (
      <main className="min-h-screen bg-gray-50 px-4 pt-28">
        <Card className="mx-auto max-w-xl">
          <CardHeader>
            <CardTitle>Administrator access required</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 text-gray-600">
            <p>This page is available only to administrator accounts.</p>
            <Link to="/" className="inline-flex items-center gap-2 text-purple-700 hover:underline">
              <ArrowLeft className="h-4 w-4" /> Return to the site
            </Link>
          </CardContent>
        </Card>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-gray-50 pt-20">
      <div className="border-b bg-white shadow-sm">
        <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-4 px-4 py-6 sm:px-6 lg:px-8">
          <div>
            <Link to="/admin-dashboard" className="mb-2 inline-flex items-center gap-2 text-sm text-purple-700 hover:underline">
              <ArrowLeft className="h-4 w-4" /> Admin workspace
            </Link>
            <p className="text-xs font-semibold uppercase tracking-[0.25em] text-purple-500">Admin workspace</p>
            <h1 className="text-3xl font-bold text-purple-900">Users</h1>
            <p className="mt-1 text-gray-600">All customer, practitioner, and administrator accounts.</p>
          </div>
          <div className="flex items-center gap-3">
            <span className="inline-flex items-center gap-2 rounded-full bg-purple-50 px-4 py-2 text-sm font-medium text-purple-700">
              <Shield className="h-4 w-4" /> Administrator
            </span>
            <Button onClick={logout} variant="outline">Log out</Button>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        <Card>
          <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-4">
            <div>
              <CardTitle className="flex items-center gap-2 text-xl">
                <Users className="h-5 w-5 text-purple-700" /> Account directory
              </CardTitle>
              <p className="mt-1 text-sm text-gray-600">
                {users.length} {users.length === 1 ? 'account' : 'accounts'} · records are view-only
              </p>
            </div>
            <Input
              aria-label="Search users"
              className="w-full sm:max-w-xs"
              placeholder="Search name, email, role, or ID"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
          </CardHeader>
          <CardContent>
            {loading ? (
              <p className="py-10 text-center text-gray-600">Loading users…</p>
            ) : error ? (
              <p role="alert" className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</p>
            ) : filteredUsers.length === 0 ? (
              <p className="py-10 text-center text-gray-600">
                {users.length ? 'No accounts match your search.' : 'No user accounts found.'}
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[720px] text-left text-sm">
                  <thead>
                    <tr className="border-b text-xs uppercase tracking-wide text-gray-500">
                      <th scope="col" className="px-3 py-3 font-semibold">Name</th>
                      <th scope="col" className="px-3 py-3 font-semibold">Email</th>
                      <th scope="col" className="px-3 py-3 font-semibold">Role</th>
                      <th scope="col" className="px-3 py-3 font-semibold">Created</th>
                      <th scope="col" className="px-3 py-3 font-semibold">Updated</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredUsers.map((account) => (
                      <tr key={account.id} className="border-b last:border-0 hover:bg-gray-50">
                        <td className="px-3 py-4 font-medium text-gray-900">{account.name || '—'}</td>
                        <td className="px-3 py-4 text-gray-700">{account.email}</td>
                        <td className="px-3 py-4">
                          <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${
                            account.role === 'admin'
                              ? 'bg-purple-100 text-purple-800'
                              : 'bg-gray-100 text-gray-700'
                          }`}>
                            {account.role}
                          </span>
                        </td>
                        <td className="px-3 py-4 text-gray-600">{formatDate(account.created_at)}</td>
                        <td className="px-3 py-4 text-gray-600">{formatDate(account.updated_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </main>
  );
};

export default AdminUsersPage;
